import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;
  #requestVersion = 0;
  #pending = false;
  #changeHandler = this.variantChanged.bind(this);
  #submitHandler = this.guardSubmit.bind(this);
  #form;


  connectedCallback() {
    this.addEventListener('change', this.#changeHandler);
    this.#form = this.closest('form');
    this.#form?.addEventListener('submit', this.#submitHandler, true);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#changeHandler);
    this.#form?.removeEventListener('submit', this.#submitHandler, true);
    this.#abortController?.abort();
    this.#requestVersion++;
  }

  get selectedOptionLabels() {
    return Array.from(this.querySelectorAll('select, fieldset input:checked')).map((input) => input.value);
  }

  getResolvedVariant() {
    const variants = JSON.parse(this.querySelector('[data-lumeya-variants]')?.textContent || '[]');
    const options = this.selectedOptionLabels;
    return variants.find((variant) => this.optionsMatch(variant, options));
  }

  // Keep the changed option and repair only incompatible downstream options.
  resolveDependentOptions(target) {
    if (this.getResolvedVariant()) return;
    const groups = Array.from(this.querySelectorAll('select, fieldset'));
    const changedIndex = groups.findIndex((group) => group === target || group.contains(target));
    if (changedIndex < 0) return;
    const selected = this.selectedOptionLabels;
    const variants = JSON.parse(this.querySelector('[data-lumeya-variants]')?.textContent || '[]');
    const candidates = variants.filter((variant) => variant.options?.length === groups.length &&
      variant.options.every((value, index) => index > changedIndex || value === selected[index]));
    const score = (variant) => variant.options.reduce((total, value, index) =>
      total + (index > changedIndex && value === selected[index] ? 1 : 0), 0);
    candidates.sort((a, b) => score(b) - score(a) || Number(b.available) - Number(a.available));
    const variant = candidates[0];
    if (!variant) return;
    groups.forEach((group, index) => {
      if (index <= changedIndex) return;
      if (group instanceof HTMLSelectElement) {
        group.value = variant.options[index];
        this.updateSelectedOption(group);
      } else {
        const input = Array.from(group.querySelectorAll('input[type="radio"]'))
          .find((option) => option.value === variant.options[index]);
        if (input) {
          input.disabled = false;
          input.checked = true;
        }
      }
    });
  }

  optionsMatch(variant, options) {
    return Boolean(variant?.id && variant.options?.length === options.length &&
      variant.options.every((value, index) => value === options[index]));
  }

  guardSubmit(event) {
    const variant = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || 'null');
    if (this.#pending || !this.optionsMatch(variant, this.selectedOptionLabels) || !variant.available) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showSelectionError(this.#pending ? 'Mise à jour de votre sélection…' : 'Cette combinaison est indisponible. Veuillez choisir une autre variante.');
      return;
    }
    this.syncVariant(variant);
  }

  showSelectionError(message) {
    let error = this.querySelector('[data-lumeya-variant-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-lumeya-variant-error', '');
      error.setAttribute('role', 'status');
      this.append(error);
    }
    error.textContent = message;
  }

  syncVariant(variant) {
    // Every cart handler must receive the same, fully resolved Shopify variant.
    this.#form?.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = String(variant.id);
      input.setAttribute('value', String(variant.id));
    });
    const scope = this.closest('quick-add-dialog, [id^="shopify-section-"]') || this.#form;
    if (!scope) return;
    scope.querySelectorAll('input[name="contact[Variante sélectionnée]"]').forEach((input) => { input.value = variant.title; });
    scope.querySelectorAll('input[name="contact[Référence produit]"]').forEach((input) => { input.value = variant.sku || ''; });
    scope.querySelectorAll('input[name="contact[URL du produit]"]').forEach((input) => {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      input.value = url.href;
    });
    scope.querySelectorAll('[data-klarna-3x]').forEach((block) => {
      const amount = block.querySelector('.klarna-3x__amount');
      if (amount) amount.textContent = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(variant.price * Math.max(1, Number(scope.querySelector('input[name="quantity"]')?.value) || 1) / 300);
      block.dataset.klarnaPriceCents = String(variant.price);
    });
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.#pending = true;
    this.querySelector('[data-lumeya-variant-error]')?.remove();

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);
    this.resolveDependentOptions(event.target);
    const choiceImage = event.target.labels?.[0]?.querySelector('img');
    const fileOf = (src) => (src || '').split('?')[0].split('/').pop();
    const choiceFile = fileOf(choiceImage?.currentSrc || choiceImage?.src);
    if (choiceFile) {
      const scope = this.closest('.shopify-section') || this;
      const thumbnail = Array.from(scope.querySelectorAll('.product-media-gallery__thumbnail')).find((item) => {
        const image = item.querySelector('img');
        return image && fileOf(image.currentSrc || image.src) === choiceFile;
      });
      thumbnail?.click();
    }

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // The URL is updated only after the complete combination has been resolved.
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    const url = new URL(this.dataset.productUrl, window.location.origin);
    const variant = this.getResolvedVariant();
    if (variant) {
      url.searchParams.set('variant', String(variant.id));
    } else {
      url.searchParams.set('option_values', this.selectedOptionsValues.join(','));
    }
    return url.href;
  }

  // Récupère la section mise à jour depuis le serveur.
  async fetchUpdatedSection(requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const version = ++this.#requestVersion;
    const options = this.selectedOptionLabels;
    const optionId = this.selectedOptionId;
    try {
      const response = await fetch(requestUrl, { signal: this.#abortController.signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Variant response: ' + response.status);
      const responseText = await response.text();
      if (version !== this.#requestVersion || !this.isConnected) return;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const text = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      const variant = JSON.parse(text || 'null');
      // Never replace the customer's selection with Shopify's fallback variant.
      if (!this.optionsMatch(variant, options)) throw new Error('Variant does not match selected options');
      const quantityScope = this.closest('.shopify-section') || this.#form; const selectedQuantity = quantityScope?.querySelector('input[name="quantity"]')?.value || "1"; html.querySelectorAll('input[name="quantity"]').forEach(input => { input.value = selectedQuantity; input.setAttribute("value", selectedQuantity); }); this.updateVariantPicker(html);
      this.dispatchEvent(new VariantUpdatedEvent(variant, optionId, {
        html,
        productId: this.dataset.productId || '',
      }));
      this.syncVariant(variant);
      this.updateUrl(variant);
      this.#pending = false;
    } catch (error) {
      if (version !== this.#requestVersion || error.name === 'AbortError') return;
      this.#pending = false;
      this.#form?.querySelectorAll('[data-ref="add-to-cart-button"]').forEach((button) => { button.disabled = true; });
      this.showSelectionError('Impossible de valider cette sélection. Choisissez à nouveau la taille ou la couleur, ou rechargez la page.');
      console.error('[Maison Lumeya] Variant selection:', error);
    }
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(variant) {
    if (Theme.template.name !== 'product' || this.closest('product-card, quick-add-dialog')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
